import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
const testimonials = [{
  quote: "PCS didn't just build us a website — they built a revenue engine. Our inbound leads increased 340% in the first quarter after launch.",
  name: 'Sarah Chen',
  title: 'CEO, Meridian Technologies',
  metric: '340% more leads'
}, {
  quote: 'We went from a generic template to a digital presence that commands respect. Prospects now come to calls already sold on our expertise.',
  name: 'Marcus Rivera',
  title: 'Founder, Apex Ventures',
  metric: '2x close rate'
}, {
  quote: 'The synergy between our new site architecture and the content strategy is unmatched. We rank for keywords we never thought possible.',
  name: 'Elena Vogt',
  title: 'CMO, NordStack',
  metric: 'Top 3 rankings'
}, {
  quote: 'Other agencies gave us mockups. PCS gave us a system — one that keeps generating authority and pipeline months after delivery.',
  name: 'David Okonkwo',
  title: 'Managing Director, Prism Capital',
  metric: '6-month ROI positive'
}, {
  quote: 'The speed alone was worth the investment. Our site went from an 8-second load time to under 1 second. Bounce rate dropped by 60%.',
  name: 'Anika Patel',
  title: 'VP Digital, Stratos Group',
  metric: '60% less bounce'
}];
export function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const paginate = useCallback((newDirection: number) => {
    setDirection(newDirection);
    setCurrent(prev => (prev + newDirection + testimonials.length) % testimonials.length);
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 6000);
    return () => clearInterval(timer);
  }, [paginate]);
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0
    })
  };
  return <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden" data-id="element-282">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" data-id="element-283" />

      <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-284">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="text-center mb-16" data-id="element-285">
          <span className="text-blue-500 font-medium tracking-widest text-sm uppercase mb-4 block" data-id="element-286">
            Client Results
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white" data-id="element-287">
            Don't Take Our Word for It.
          </h2>
        </motion.div>

        {/* Carousel */}
        <div className="relative min-h-[320px] md:min-h-[280px] flex items-center" data-id="element-288">
          <AnimatePresence mode="wait" custom={direction} data-id="element-289">
            <motion.div key={current} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-290">
              <div className="bg-[#111111] border border-white/10 rounded-sm p-8 md:p-12 w-full text-center relative" data-id="element-291">
                <div className="absolute top-6 left-8 md:left-12" data-id="element-292">
                  <Quote size={32} className="text-blue-500/20" data-id="element-293" />
                </div>

                <p className="text-lg md:text-xl text-neutral-300 leading-relaxed mb-8 max-w-3xl mx-auto pt-6" data-id="element-294">
                  "{testimonials[current].quote}"
                </p>

                <div className="flex flex-col items-center gap-2" data-id="element-295">
                  <span className="font-display font-semibold text-white text-lg" data-id="element-296">
                    {testimonials[current].name}
                  </span>
                  <span className="text-neutral-500 text-sm" data-id="element-297">
                    {testimonials[current].title}
                  </span>
                  <span className="mt-2 inline-block bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-medium px-3 py-1 rounded-full" data-id="element-298">
                    {testimonials[current].metric}
                  </span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-6 mt-10" data-id="element-299">
          <button onClick={() => paginate(-1)} className="w-10 h-10 border border-white/10 rounded-full flex items-center justify-center text-neutral-400 hover:text-white hover:border-white/30 transition-colors" aria-label="Previous testimonial" data-id="element-300">
            <ChevronLeft size={18} data-id="element-301" />
          </button>

          <div className="flex items-center gap-2" data-id="element-302">
            {testimonials.map((_, index) => <button key={index} onClick={() => {
            setDirection(index > current ? 1 : -1);
            setCurrent(index);
          }} className={`h-1.5 rounded-full transition-all duration-300 ${index === current ? 'w-8 bg-blue-500' : 'w-1.5 bg-white/20 hover:bg-white/40'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-303" />)}
          </div>

          <button onClick={() => paginate(1)} className="w-10 h-10 border border-white/10 rounded-full flex items-center justify-center text-neutral-400 hover:text-white hover:border-white/30 transition-colors" aria-label="Next testimonial" data-id="element-304">
            <ChevronRight size={18} data-id="element-305" />
          </button>
        </div>
      </div>
    </section>;
}